import { Component, Inject, Injector } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { WizardStepComponent } from 'mbs-ui-kit';
import { ComputerTagsFacade } from 'mbs-ui-shared/src/lib/facades/computer.tags.facade';
import { NotificationPlanRule } from 'mbs-ui-shared/src/lib/models/Notification';
import Computer from 'mbs-ui-shared@models/Computer';
import { CustomAlertingSettings } from 'mbs-ui-shared@models/rmm-custom-notification-model';
import { Tag } from 'mbs-ui-shared@models/tag';
import { SelectedEntities } from 'mbs-ui-shared@store/rmmGroupActionWizard/model';
import { ComputerFromGroupTaskComputers } from 'mbs-ui-shared@store/rmmGroupTasks/model';
import { Observable, combineLatest, filter } from 'rxjs';
import { LoadRmmCustomNotificationWizardService } from '../../utility/load-rmm-custom-notification-wizard.service';

@UntilDestroy()
@Component({
  selector: 'mbs-show-summary',
  templateUrl: './show-summary.component.html'
})
export class ShowSummaryComponent {
  public resultInfo: Partial<CustomAlertingSettings>;
  public providerEmail: string;
  public showProvider = false;
  public showEmailRecipients = false;
  public showPushRecipients = false;
  public pushRecipientNames: string;
  public totalCount: number;
  public applyToMode = false;
  public profileInfo = null;

  constructor(
    @Inject(WizardStepComponent) protected parent: WizardStepComponent,
    protected injector: Injector,
    private loadRmmCustomNotificationWizardService: LoadRmmCustomNotificationWizardService,
    private tagsFacade: ComputerTagsFacade,
    private i18n: I18NextPipe
  ) {
    this.loadRmmCustomNotificationWizardService.providerEmail.pipe(untilDestroyed(this)).subscribe((value) => (this.providerEmail = value));

    combineLatest([this.loadRmmCustomNotificationWizardService.applyTo, this.loadRmmCustomNotificationWizardService.profileName])
      .pipe(
        filter(() => this.loadRmmCustomNotificationWizardService.showApplyToStep),
        untilDestroyed(this)
      )
      .subscribe(([applyTo, name]) => {
        this.applyToMode = true;
        this.profileInfo = {
          name,
          applyTo: this.getApplyToSummary(applyTo)
        };
      });

    combineLatest([
      this.loadRmmCustomNotificationWizardService.notificationsToSave,
      this.loadRmmCustomNotificationWizardService.recipientsPayload
    ])
      .pipe(
        filter(([settings, recipientsPayload]) => !!settings && !!recipientsPayload),
        untilDestroyed(this)
      )
      .subscribe(([settings, recipientsPayload]) => {
        this.providerEmail = this.loadRmmCustomNotificationWizardService.providerEmail.getValue();
        this.totalCount = this.loadRmmCustomNotificationWizardService.totalCount;
        this.pushRecipientNames = this.loadRmmCustomNotificationWizardService.pushAdminNameList;

        this.resultInfo = {
          ...settings,
          ...recipientsPayload
        };

        this.showProvider = [
          NotificationPlanRule.Full,
          NotificationPlanRule.Provider,
          NotificationPlanRule.Provider ^ NotificationPlanRule.SpecifiedEmails
        ].includes(this.resultInfo?.recipient?.type);

        this.showEmailRecipients =
          [
            NotificationPlanRule.Provider ^ NotificationPlanRule.SpecifiedEmails,
            NotificationPlanRule.SpecifiedEmails,
            NotificationPlanRule.Users
          ].includes(this.resultInfo?.recipient?.type) && this.resultInfo?.recipient?.emails?.some((value) => value !== this.providerEmail);

        this.showPushRecipients = this.resultInfo?.pushNotification?.enabled;
      });
  }

  getApplyToSummary(applyTo: SelectedEntities): { includeInfo: string; excludeInfo: string; tags: number[] } {
    if (!applyTo) return null;

    if (applyTo?.allCompanies) {
      return {
        includeInfo: this.i18n.transform('shared:rmm.groupActions.updatePolicy.allCompaniesInfo'),
        excludeInfo: this.getApplyToComputersInfo(applyTo?.computers),
        tags: applyTo?.computerTags ?? []
      };
    }

    if (applyTo?.companies?.length) {
      let textInfo = this.i18n.transform('shared:rmm.groupActions.updatePolicy.companyListInfo');
      applyTo?.companies.forEach((company, index) => (textInfo += index !== 0 ? `, ${company.name}` : company.name));

      return {
        includeInfo: textInfo,
        excludeInfo: this.getApplyToComputersInfo(applyTo?.computers),
        tags: applyTo?.computerTags ?? []
      };
    }

    return {
      includeInfo: this.getApplyToComputersInfo(applyTo?.computers, false),
      excludeInfo: this.getApplyToComputersInfo(applyTo?.computers),
      tags: applyTo?.computerTags ?? []
    };
  }

  public getApplyToComputersInfo(computers: Partial<ComputerFromGroupTaskComputers>[], isExcluded = true): string {
    if (!computers?.length) return null;

    const includeText = this.i18n.transform('shared:rmm.groupActions.includedComputersInfo');
    const excludeText = this.i18n.transform('shared:rmm.groupActions.excludedComputersInfo');

    let textInfo = isExcluded ? excludeText : includeText;

    textInfo += ` ${computers
      .filter((comp) => comp.disabled === isExcluded)
      .map((computer) => Computer.getComputerName(computer))
      .join(', ')}`;

    if (isExcluded && excludeText.trim() === textInfo.trim()) return null;
    if (!isExcluded && includeText.trim() === textInfo.trim()) return null;

    return textInfo;
  }

  public getTagById(id: number): Observable<Tag> {
    return this.tagsFacade.tagById$(id).pipe(untilDestroyed(this));
  }
}
